import React, { useCallback, useRef } from 'react';

import { useChatAttachments } from '../../../../../../../../store/dashboard/askAica/useChatAttachments';
import { ASK_AICA_SURFACE, useAskAicaSurface } from '../../context/AskAicaSurfaceContext';

/** Wires the composer's attach control, file input and drop zone to the chat attachment state machine. */
export const useComposerAttachments = (disabled: boolean) => {
    const { surface, selectedConversationId, fileContextOrgId, currentCompany, bindConversation } = useAskAicaSurface();
    const isPortfolio = surface === ASK_AICA_SURFACE.PORTFOLIO;
    const fileInputRef = useRef<HTMLInputElement>(null);

    const getCompanyName = useCallback(
        (orgId: string | null) =>
            currentCompany && orgId && currentCompany.org_id === orgId ? currentCompany.name ?? undefined : undefined,
        [currentCompany],
    );

    const attachments = useChatAttachments({
        conversationId: selectedConversationId,
        source: isPortfolio ? 'portfolio' : 'lead',
        orgId: isPortfolio ? undefined : fileContextOrgId,
        onConversationBound: (conversationId) => bindConversation?.(conversationId),
        getCompanyName,
    });
    const { addFiles } = attachments;

    const openPicker = useCallback(() => fileInputRef.current?.click(), []);

    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            addFiles(Array.from(event.target.files ?? []));
            // Reset so picking the same file again still fires onChange.
            event.target.value = '';
        },
        [addFiles],
    );

    const handleDragOver = useCallback((event: React.DragEvent) => event.preventDefault(), []);

    const handleDrop = useCallback(
        (event: React.DragEvent) => {
            // Always cancel the default, or the browser navigates to the dropped file.
            event.preventDefault();
            if (disabled) return;
            addFiles(Array.from(event.dataTransfer.files));
        },
        [addFiles, disabled],
    );

    return { attachments, fileInputRef, openPicker, handleFileInputChange, handleDragOver, handleDrop };
};
